import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useSetStaffAccess,
  useStaffAccounts,
  type StaffAccess as Access,
} from "@/features/admin";
import { useUser } from "@/features/auth/hooks/use-user";
import { useState } from "react";
import { toast } from "sonner";

type Choice = Access | "none";

const LABELS: Record<Choice, string> = {
  admin: "Administrateur",
  read: "Lecture seule",
  none: "Aucun accès",
};

/** A change waiting for its confirmation (anything that involves `admin`). */
type Pending = { email: string; from: Choice; to: Choice };

/**
 * Who reaches this back-office, and how far — the administrators and the
 * read-only accounts (sales: the clients, their dashboard and their calls, the
 * global figures and the control centre; nothing technical, no explorer, no
 * write).
 *
 * Granted to an account that already exists (the form beside creates one),
 * changed or taken away from the list. The server refuses your own access and
 * the last administrator (`admin/services/staff-access.ts`); this screen only
 * greys them out. A change applies within the account's next token renewal
 * (15 minutes at most). Anything that makes or unmakes an administrator asks
 * first: it hands over — or takes away — every tenant at once.
 */
export function StaffAccess() {
  const { data: accounts = [], isLoading } = useStaffAccounts();
  const { data: me } = useUser();
  const setStaffAccess = useSetStaffAccess();
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState<Access>("read");
  const [pending, setPending] = useState<Pending | null>(null);

  const admins = accounts.filter((account) => account.access === "admin");

  const apply = async ({ email: target, to }: Pending) => {
    try {
      await setStaffAccess.mutateAsync({ email: target, access: to });
      toast.success(
        to === "none"
          ? `Accès retiré à ${target}`
          : `${target} : ${LABELS[to].toLowerCase()}`,
      );
      if (target === email) setEmail("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "La modification a échoué",
      );
    }
  };

  const request = (change: Pending) => {
    if (change.from === change.to) return;
    if (change.from === "admin" || change.to === "admin") setPending(change);
    else void apply(change);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accès au back-office</CardTitle>
        <CardDescription>
          Les administrateurs ont tout. La lecture seule, pour les sales : les
          clients, leur dashboard et leurs appels, la vue globale et le centre
          de contrôle — rien de technique, pas d'explorateur, aucune
          modification.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const target = email.trim();
            if (!target) return;
            const current = accounts.find(
              (account) => account.email === target,
            );
            request({
              email: target,
              from: current?.access ?? "none",
              to: access,
            });
          }}
        >
          <Input
            type="email"
            className="min-w-48 flex-1"
            placeholder="prenom@heyup.ai"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Select
            value={access}
            onValueChange={(value) => setAccess(value as Access)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">{LABELS.read}</SelectItem>
              <SelectItem value="admin">{LABELS.admin}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="submit"
            disabled={!email.trim() || setStaffAccess.isPending}
          >
            Donner l'accès
          </Button>
        </form>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <ul className="divide-y rounded-md border">
            {accounts.map((account) => {
              const self = account.user_id === me?.id;
              const lastAdmin =
                account.access === "admin" && admins.length <= 1;
              return (
                <li
                  key={account.user_id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 truncate text-sm font-medium">
                      {account.full_name || account.email}
                      {self && <Badge variant="secondary">Vous</Badge>}
                    </div>
                    {account.full_name && (
                      <div className="text-muted-foreground truncate text-xs">
                        {account.email}
                      </div>
                    )}
                  </div>
                  <Select
                    value={account.access}
                    disabled={self || lastAdmin || setStaffAccess.isPending}
                    onValueChange={(value) =>
                      request({
                        email: account.email,
                        from: account.access,
                        to: value as Choice,
                      })
                    }
                  >
                    <SelectTrigger
                      className="w-40 shrink-0"
                      title={
                        self
                          ? "Votre propre accès ne se modifie pas ici"
                          : lastAdmin
                            ? "Le dernier administrateur le reste"
                            : undefined
                      }
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{LABELS.admin}</SelectItem>
                      <SelectItem value="read">{LABELS.read}</SelectItem>
                      <SelectItem value="none">{LABELS.none}</SelectItem>
                    </SelectContent>
                  </Select>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <Dialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.to === "admin"
                ? "Nommer un administrateur ?"
                : "Retirer un administrateur ?"}
            </DialogTitle>
            <DialogDescription>
              {pending?.to === "admin"
                ? `${pending?.email} aura accès à tout le back-office : tous les clients, la configuration, l'explorateur, et la gestion de ces accès.`
                : `${pending?.email} passera en « ${pending ? LABELS[pending.to].toLowerCase() : ""} ».`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Annuler
            </Button>
            <Button
              variant={pending?.to === "admin" ? "default" : "destructive"}
              disabled={setStaffAccess.isPending}
              onClick={async () => {
                if (pending) await apply(pending);
                setPending(null);
              }}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
